/**
 * Timed power-up types and tuning.
 *
 * Three pickup effects ride the existing PowerUpSystem alongside
 * homing-missiles / wide-mines:
 *
 *  - `overdrive` — 5 s engine boost. CLIENT-authoritative movement means the
 *    client applies the multiplier in VehicleController while the server
 *    relaxes its movement sanity checks (teleport + forward-speed clamps in
 *    ArenaRoom's "state" handler) for the effect window + a grace period.
 *  - `spread5`  — 5 s five-projectile fan on the primary weapon. Each pellet
 *    is a normal projectile at SPREAD5_DAMAGE_SCALE of the weapon's damage.
 *  - `armour`   — instant over-armour grant. A depletable pool on PlayerState
 *    that absorbs weapon damage (projectiles + mines) before health.
 *    Collision/ram damage intentionally bypasses armour.
 *
 * Follows the boost.ts pattern: one config object, named exports derived
 * from it (single place to tune, greppable constants everywhere else).
 */

export type PowerUpType =
  | "homing-missiles"
  | "wide-mines"
  | "overdrive"
  | "spread5"
  | "armour"
  | "ricochet"
  | "shockwave";

export const POWERUP_CONFIG = {
  overdrive: {
    durationMs: 5000,
    /** Multiplier on top speed while active (stacks with regular boost). */
    speedMultiplier: 1.5,
    /** Multiplier on acceleration while active. */
    accelMultiplier: 2.0,
    /**
     * Extra window (ms) after expiry during which the server still clamps at
     * the boosted bound. Covers patch latency + client-side deceleration so an
     * expiring overdrive never trips the anti-teleport check.
     */
    clampGraceMs: 750,
  },
  spread5: {
    durationMs: 5000,
    /** Per-pellet damage as a fraction of the weapon's normal damage. */
    damageScale: 0.5,
    /** Half of the total fan angle (radians). Total spread = 20°. */
    halfAngleRad: (10 * Math.PI) / 180,
  },
  armour: {
    /** Armour points granted per pickup. */
    grant: 50,
    /** Hard cap on the armour pool. */
    max: 100,
  },
} as const;

export const OVERDRIVE_DURATION_MS      = POWERUP_CONFIG.overdrive.durationMs;
export const OVERDRIVE_SPEED_MULTIPLIER = POWERUP_CONFIG.overdrive.speedMultiplier;
export const OVERDRIVE_ACCEL_MULTIPLIER = POWERUP_CONFIG.overdrive.accelMultiplier;
export const OVERDRIVE_CLAMP_GRACE_MS   = POWERUP_CONFIG.overdrive.clampGraceMs;

export const SPREAD5_DURATION_MS   = POWERUP_CONFIG.spread5.durationMs;
export const SPREAD5_DAMAGE_SCALE  = POWERUP_CONFIG.spread5.damageScale;

/** Five evenly spaced fan angles (radians): ±10°, ±5°, 0. */
export const SPREAD5_ANGLES: readonly number[] = [
  -POWERUP_CONFIG.spread5.halfAngleRad,
  -POWERUP_CONFIG.spread5.halfAngleRad / 2,
  0,
  POWERUP_CONFIG.spread5.halfAngleRad / 2,
  POWERUP_CONFIG.spread5.halfAngleRad,
];

export const ARMOUR_PICKUP_GRANT = POWERUP_CONFIG.armour.grant;
export const ARMOUR_MAX          = POWERUP_CONFIG.armour.max;

/**
 * Absorb `damage` with an armour pool. Mutates `pool.armour` down by the
 * absorbed amount and returns the damage that remains for health.
 */
export function absorbWithArmour(pool: { armour: number }, damage: number): number {
  if (damage <= 0) return 0;
  if (pool.armour <= 0) return damage;
  const absorbed = Math.min(pool.armour, damage);
  pool.armour -= absorbed;
  return damage - absorbed;
}

/** Health fraction below which bots start preferring armour pickups. */
export const BOT_ARMOUR_PREFERENCE_HEALTH = 0.6;
/** Effective-distance multiplier for armour pickups when the bot is hurt. */
export const BOT_ARMOUR_PREFERENCE_SCALE = 0.5;

/**
 * Effective distance used by bot pickup seeking. A hurt bot sees armour
 * pickups at half distance, which mildly biases Survivor/Hunter toward armour
 * without rebalancing the archetypes.
 */
export function pickupSeekDistance(
  distance: number,
  type: string | undefined,
  selfHealthFrac: number,
): number {
  if (type === "armour" && selfHealthFrac < BOT_ARMOUR_PREFERENCE_HEALTH) {
    return distance * BOT_ARMOUR_PREFERENCE_SCALE;
  }
  return distance;
}
